import React from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useSnackbar } from "notistack";

import { List, CircularProgress } from "@mui/material";

import Layout from "components/common/Layout";

import OrderStatesListItem from "./OrderStatesListItem";

const QUERY = gql`
  query ($withDeleted: Boolean) {
    orderStates(withDeleted: $withDeleted) {
      id

      ...OrderStatesListItem_OrderState
    }
  }

  ${OrderStatesListItem.fragments.orderState}
`;

const MUTATION = gql`
  mutation ($ids: [ID]!) {
    ReorderOrderStates(orderStatesIds: $ids) {
      orderStates {
        id
      }
    }
  }
`;

export default function OrderStatesList() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { data, loading } = useQuery(QUERY, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: {
      withDeleted: true,
    },
  });

  const [reorderOrderStates] = useMutation(MUTATION);

  const onDragEnd = React.useCallback(
    async (result) => {
      // dropped outside the list
      if (!result.destination) return;

      const snackbarKey = enqueueSnackbar("Enregistrement en cours …", {
        persist: true,
        action: <CircularProgress size={30} color="inherit" />,
      });

      const reordered = Array.from(data.orderStates);
      const [removed] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, removed);

      await reorderOrderStates({
        variables: {
          ids: reordered.map(({ id }) => id),
        },
        optimisticResponse: {
          ReorderOrderStates: {
            orderStates: reordered,
            __typename: "ReorderOrderStates",
          },
        },
        update: (store, { data: { ReorderOrderStates } }) => {
          const getOrderState = (orderStateId) =>
            data.orderStates.find(({ id }) => id === orderStateId);

          const orderStates = ReorderOrderStates.orderStates
            .map(({ id }) => getOrderState(id))
            .filter(Boolean);

          store.writeQuery({
            query: QUERY,
            variables: {
              withDeleted: true,
            },
            data: {
              ...data,
              orderStates,
            },
          });
        },
      });

      closeSnackbar(snackbarKey);
    },
    [data, reorderOrderStates, enqueueSnackbar, closeSnackbar]
  );

  if (loading) return <Layout.Loading />;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <List dense>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {data?.orderStates.map((node, index) => (
                <Draggable key={node.id} draggableId={node.id} index={index}>
                  {(provided, snapshot) => (
                    <OrderStatesListItem
                      orderState={node}
                      handleProps={provided.dragHandleProps}
                      ref={provided.innerRef}
                      ContainerProps={provided.draggableProps}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </List>
    </DragDropContext>
  );
}
